import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Loader2, Calendar as CalendarIcon, BarChart3, RefreshCw, ArrowLeft } from 'lucide-react';
import { formatCurrency, cn } from '../lib/utils';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComparisonPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [period, setPeriod] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [comparison, setComparison] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
      // Select first 2 locations by default
      if (res.data.length >= 2) {
        setSelectedLocations([res.data[0].id, res.data[1].id]);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationToggle = (locationId) => {
    setSelectedLocations(prev => {
      if (prev.includes(locationId)) {
        return prev.filter(id => id !== locationId);
      }
      return [...prev, locationId];
    });
  };

  const handleCompare = async () => {
    if (selectedLocations.length < 2) {
      return;
    }

    setComparing(true);
    try {
      const res = await axios.get(`${API}/comparisons`, {
        params: {
          location_ids: selectedLocations.join(','),
          date: format(selectedDate, 'yyyy-MM-dd'),
          period
        }
      });
      setComparison(res.data);
    } catch (error) {
      console.error('Failed to compare locations:', error);
    } finally {
      setComparing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-emerald-600 bg-emerald-50';
    if (score >= 70) return 'text-lime-600 bg-lime-50';
    if (score >= 55) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  const getMetricBest = (metricName) => {
    if (!comparison?.locations) return null;
    let best = null;
    let bestValue = -Infinity;
    
    comparison.locations.forEach(loc => {
      const value = loc.metrics?.[metricName] || 0;
      if (value > bestValue) {
        bestValue = value;
        best = loc.location_id;
      }
    });
    
    return best;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  // Get all metric names from comparison
  const metricNames = comparison?.locations?.[0]?.metrics 
    ? Object.keys(comparison.locations[0].metrics) 
    : [];

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="comparison-page">
        {/* Header with Back Button */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="mb-4 text-slate-600 hover:text-slate-900"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit']">
            Location Comparison
          </h1>
          <p className="text-slate-600 mt-1">Compare performance metrics side by side</p>
        </div>

        {/* Controls */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="space-y-6">
              {/* Location Selection */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3">Select Locations (min 2)</h3>
                <div className="flex flex-wrap gap-3">
                  {locations.map(loc => (
                    <label
                      key={loc.id}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors",
                        selectedLocations.includes(loc.id)
                          ? "border-violet-500 bg-violet-50"
                          : "border-slate-200 hover:border-violet-300"
                      )}
                      data-testid={`location-checkbox-${loc.id}`}
                    >
                      <Checkbox
                        checked={selectedLocations.includes(loc.id)}
                        onCheckedChange={() => handleLocationToggle(loc.id)}
                      />
                      <span className="text-sm font-medium">{loc.name}</span>
                      <Badge variant="outline" className="text-xs">{loc.code}</Badge>
                    </label>
                  ))}
                </div>
              </div>

              {/* Period & Date */}
              <div className="flex flex-wrap items-center gap-4">
                <Tabs value={period} onValueChange={setPeriod}>
                  <TabsList>
                    <TabsTrigger value="daily">Daily</TabsTrigger>
                    <TabsTrigger value="weekly">Weekly</TabsTrigger>
                    <TabsTrigger value="monthly">Monthly</TabsTrigger>
                  </TabsList>
                </Tabs>

                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" data-testid="comparison-date-btn">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(selectedDate, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        if (date) {
                          setSelectedDate(date);
                          setDatePickerOpen(false);
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Button
                  onClick={handleCompare}
                  disabled={selectedLocations.length < 2 || comparing}
                  className="bg-violet-600 hover:bg-violet-700"
                  data-testid="compare-btn"
                >
                  {comparing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <BarChart3 className="w-4 h-4 mr-2" />
                  )}
                  Compare
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Comparison Results */}
        {comparison && (
          <Card data-testid="comparison-results">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-violet-600" />
                Comparison Results
              </CardTitle>
              <CardDescription>
                {period.charAt(0).toUpperCase() + period.slice(1)} comparison for {format(selectedDate, "PPP")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {comparison.locations.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-slate-500">No data available for the selected period</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Metric</th>
                        {comparison.locations.map(loc => (
                          <th key={loc.location_id} className="text-center py-3 px-4 font-semibold text-slate-900 min-w-[150px]">
                            {loc.location_name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Overall Score */}
                      <tr className="border-b border-slate-100 bg-violet-50/30">
                        <td className="py-4 px-4 font-semibold text-violet-700">Overall Score</td>
                        {comparison.locations.map(loc => {
                          const isBest = comparison.locations.every(l => l.average_score <= loc.average_score);
                          return (
                            <td key={loc.location_id} className="text-center py-4 px-4">
                              <span className={cn(
                                "inline-flex items-center justify-center w-16 h-16 rounded-xl font-mono font-bold text-xl",
                                getScoreColor(loc.average_score),
                                isBest && "ring-2 ring-violet-500"
                              )}>
                                {Math.round(loc.average_score)}
                              </span>
                            </td>
                          );
                        })}
                      </tr>

                      {/* Total Sales */}
                      <tr className="border-b border-slate-100">
                        <td className="py-3 px-4 text-slate-600">Total Sales</td>
                        {comparison.locations.map(loc => {
                          const isBest = comparison.locations.every(l => l.total_sales <= loc.total_sales);
                          return (
                            <td key={loc.location_id} className={cn(
                              "text-center py-3 px-4 font-mono",
                              isBest && "text-emerald-600 font-semibold"
                            )}>
                              {formatCurrency(loc.total_sales)}
                              {isBest && <span className="ml-2 text-xs">★</span>}
                            </td>
                          );
                        })}
                      </tr>

                      {/* Budget Attainment */}
                      <tr className="border-b border-slate-100">
                        <td className="py-3 px-4 text-slate-600">Budget Attainment</td>
                        {comparison.locations.map(loc => {
                          const isBest = comparison.locations.every(l => l.budget_attainment <= loc.budget_attainment);
                          return (
                            <td key={loc.location_id} className={cn(
                              "text-center py-3 px-4 font-mono",
                              isBest && "text-emerald-600 font-semibold"
                            )}>
                              {loc.budget_attainment.toFixed(1)}%
                              {isBest && <span className="ml-2 text-xs">★</span>}
                            </td>
                          );
                        })}
                      </tr>

                      {/* Entry Count */}
                      <tr className="border-b border-slate-100">
                        <td className="py-3 px-4 text-slate-600">Entries</td>
                        {comparison.locations.map(loc => (
                          <td key={loc.location_id} className="text-center py-3 px-4 font-mono">
                            {loc.entry_count}
                          </td>
                        ))}
                      </tr>

                      {/* Individual Metrics */}
                      {metricNames.map(metricName => {
                        const bestId = getMetricBest(metricName);
                        return (
                          <tr key={metricName} className="border-b border-slate-100">
                            <td className="py-3 px-4 text-slate-600">{metricName}</td>
                            {comparison.locations.map(loc => {
                              const value = loc.metrics?.[metricName] || 0;
                              const isBest = loc.location_id === bestId;
                              const isPercentage = metricName.includes('%') || 
                                metricName.includes('Rate') || 
                                metricName.includes('Attainment') ||
                                metricName.includes('Conversion') ||
                                metricName.includes('Flow');
                              const isCurrency = metricName.includes('Revenue') || 
                                metricName.includes('Sales') || 
                                metricName.includes('ATV') ||
                                metricName.includes('Per Cap');
                              
                              return (
                                <td key={loc.location_id} className={cn(
                                  "text-center py-3 px-4 font-mono",
                                  isBest && "text-emerald-600 font-semibold"
                                )}>
                                  {isCurrency ? formatCurrency(value) : isPercentage ? `${value.toFixed(1)}%` : value.toFixed(2)}
                                  {isBest && <span className="ml-2 text-xs">★</span>}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {!comparison && (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">Select locations to compare</h3>
                <p className="text-slate-500">Choose at least 2 locations and click Compare to see side-by-side metrics</p>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
